/**
 * Core MuiDataGrid Component
 *
 * A thin, opinionated wrapper around `@mui/x-data-grid` that:
 *  - Disables the print toolbar button by default (enterprise policy)
 *  - Exposes the full MUI DataGrid API via spread props
 *  - Applies a consistent container height / width
 *
 * Use this instead of importing DataGrid directly from @mui/x-data-grid
 * so that global toolbar policy overrides are always in effect.
 *
 * @example
 *   <MuiDataGrid rows={rows} columns={columns} />
 *
 *   // Override height
 *   <MuiDataGrid rows={rows} columns={columns} height={500} />
 *
 *   // Re-enable print if explicitly needed
 *   <MuiDataGrid rows={rows} columns={columns} printOptions={{ disableToolbarButton: false }} />
 */
import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import type { DataGridProps } from '@mui/x-data-grid';

// ── Props ───────────────────────────────────────────────────────────────
export interface MuiDataGridProps extends Omit<DataGridProps, 'slotProps'> {
  /**
   * Container height in pixels.
   * @default 400
   */
  height?: number | string;

  /**
   * Container width. Defaults to 100% of the parent.
   * @default '100%'
   */
  width?: number | string;

  /**
   * Override print export options forwarded to the toolbar slot.
   * The print button is disabled by default — pass `{ disableToolbarButton: false }`
   * to restore it.
   */
  printOptions?: { disableToolbarButton?: boolean; [key: string]: unknown };

  /**
   * Any additional `slotProps` you want to merge in.
   * The `toolbar.printOptions` entry from the `printOptions` prop takes precedence.
   */
  slotProps?: DataGridProps['slotProps'];
}

// ── Component ───────────────────────────────────────────────────────────
export function MuiDataGrid({
  height = 400,
  width = '100%',
  printOptions,
  slotProps,
  showToolbar = true,
  ...rest
}: MuiDataGridProps) {
  const mergedSlotProps: DataGridProps['slotProps'] = {
    ...slotProps,
    toolbar: {
      ...(slotProps?.toolbar as object | undefined),
      printOptions: {
        disableToolbarButton: true,           // enterprise default — hide print
        ...(slotProps?.toolbar as any)?.printOptions,
        ...printOptions,                       // caller override wins
      },
    },
  };

  return (
    <div style={{ height, width }}>
      <DataGrid
        showToolbar={showToolbar}
        slotProps={mergedSlotProps}
        {...rest}
      />
    </div>
  );
}

export default MuiDataGrid;
